import { useEffect, useRef, ReactNode } from 'react'
import ReactGA from 'react-ga4'
import useFetchInitialCategoriesData from '../hooks/categories/useInitialCategoriesData'
import useInitialCatalogData from '../hooks/catalog/useInitialCatalogData'
import { fetchConfig, manageErrors, setSessionId } from '../redux/slices/config'
import { useAppDispatch, useAppSelector, useSessionGuard } from '../hooks'
import { ErrorMessageTranslation } from '../interfaces'
import getUrlParams from '../utils/getUrlParams'

/**
 * This component is in charge of executing the redux loadCatalogue action, this is the wrapper
 * of all the containers,
 */
interface ConfigProps {
  children: ReactNode
}

const Config = ({ children }: ConfigProps) => {
  const slug = useAppSelector((state) => state.defaultSlice.storeName)
  const config = useAppSelector((state) => state.defaultSlice.config)

  const dispatch = useAppDispatch()
  const { storeName: slugFromUrl, language } = getUrlParams(
    window.location.pathname,
  )
  const { requiresSession, sessionId } = useSessionGuard()

  const fetchInitialCatalogDataAction = useInitialCatalogData()
  const fetchInitialCategoriesDataAction = useFetchInitialCategoriesData()

  /**
   * The `(slug, sessionId)` pair this instance has already prefetched, or
   * `null` before the first one (COM-974).
   *
   * **A ref the effect writes itself, not a value the response writes back.**
   * The other shape the ticket suggested — gate on `sessionData` already being
   * in the store — reads a value `onSessionCompleted` writes one round trip
   * later (`hooks/catalog/useInitialCatalogData.ts`,
   * `hooks/categories/useInitialCategoriesData.ts`), so a shopper who crosses
   * the boundary and comes back before that response lands would find nothing
   * there and issue a second prefetch. That is the load-bearing difference and
   * it is pinned in two suites: rewriting this gate to consult `sessionData`
   * fails tests in `hooks/tests/sessionGuard.test.tsx` and in
   * `routes/tests/sessionCrossingBootGate.test.tsx`. No count is given because
   * a count rots on the next test either file gains; run the mutant.
   *
   * ⚠️ **Do not read that as the *statement order* inside the block mattering.
   * It does not, and no test can make it.** Writing the ref after the two
   * prefetch calls instead of before them passes the whole suite — measured —
   * and it takes *two* properties to close that, not one. The first is that
   * both calls are synchronous invocations inside a synchronous effect body,
   * so nothing can re-enter between the write and the calls. The second is
   * that both prefetch hooks are declared `async`, and an `async` function
   * cannot throw to its caller — it returns a rejected promise instead. Were
   * either hook rewritten as a plain function that throws before its first
   * `await`, the throw would escape this block and the ordering would become
   * observable: the ref would hold a pair nothing had fetched. That is the
   * condition under which this "unkillable" label expires.
   *
   * An earlier revision of this comment credited the wrong test with the wrong
   * property. The "never-resolving prefetch" test in `sessionGuard.test.tsx`
   * is not inert and its mock is not a bare `jest.fn()` — it is
   * `mockImplementation(() => new Promise(() => {}))`, and it *is* a kill case
   * for the `sessionData` gate, because a gate reading the store would be open
   * for the whole round trip it holds in flight. What that test cannot pin is
   * statement order, which is a different thing from pinning nothing. Nor is
   * the return value ignored any more: the gate now chains `.then` onto it to
   * release the pair on failure (COM-974, below).
   *
   * **The pair, not a boolean, and both halves earn their place.** A
   * storefront that moves the shopper to a genuinely new session has to load
   * that session's catalogue, so what is recorded is which pair was fetched
   * and not whether anything was. Neither half is decorative: collapsing the
   * key to a boolean fails several tests, and dropping the *slug* half —
   * keying on the session id alone — fails `prefetches again when the slug
   * moves and the session id does not`. That last one needs a fixture that moves
   * `window.history` and then clicks, because `slugFromUrl` is read from
   * `window.location` while a `<Link>` moves only the router's location.
   *
   * `null` rather than `''` as the initial value, and **for legibility, not to
   * avoid a collision** — there is no collision available to avoid. Every key
   * this code writes is `JSON.stringify` of a two-element array, so the
   * shortest one it can produce is `'["",""]'` for the empty-slug pair;
   * nothing it writes is ever `''`. `null` is chosen because "nothing
   * prefetched yet" reads better as an absence than as an empty string, and
   * because the release callback below assigns it back.
   *
   * **`JSON.stringify` rather than a `|`-joined template literal**, so that the
   * encoding cannot be ambiguous. Both halves come out of the URL and neither
   * is validated against a charset, so a joined key collides for
   * `('a|b', 'c')` and `('a', 'b|c')` — reachable only through a slug or
   * session id containing the separator, which is far-fetched, but the
   * unambiguous encoding costs nothing. Worth knowing if this ever reverts:
   * the failure would have been a *suppressed* prefetch and never a
   * misdirected one, since the two fetch calls take `sessionId` and
   * `slugFromUrl` directly and never see this key.
   *
   * Same instrument as `seenRefreshNonceRef` in
   * `hooks/orders/useGetOrderDetail.ts`, and for that ref's stated reason: a
   * dependency array cannot express "once per visit" when the deps themselves
   * move, because an effect re-runs on a dep that moved and came back. (Not
   * `lastOrderIdRef` in the same file, which an earlier revision of this
   * comment cited. That ref's two reasons are about which *instance* of a hook
   * may act on a store-wide edge and about reading an id that survives a
   * `notFound`; neither is about dependency arrays, and it never carried a
   * `StrictMode` justification to retract.)
   *
   * ⚠️ **Not for the `StrictMode` reason, which is false here and at that
   * site.** React 18 double-invokes mount effects only on a **concurrent**
   * root, and `index.tsx` still mounts through legacy `ReactDOM.render` —
   * there is no `createRoot` anywhere in `packages/`, and the call carries an
   * `eslint-disable react/no-deprecated`. So the `<StrictMode>` wrapper is
   * real and **inert for effects**: the boot prefetch went out once per page
   * load in development before this gate and once after, exactly as in
   * production. What double-invokes is the **suite** —
   * `@testing-library/react` (the `16.1.0` this package declares, and the copy
   * the suite actually loads — check `packages/cinnamon/node_modules`, not the
   * hoisted root, which holds a different version that nothing here resolves
   * to) mounts through `createRoot` — which is why
   * a StrictMode test can pass while the app never exercises the path. The
   * repo already carried this correction elsewhere before this ticket re-made
   * the mistake; see `hooks/orders/useOrderConfirmation.ts` and
   * `hooks/orders/tests/useOrderConfirmationRefresh.test.tsx`. No count is
   * given: the belief has recurred often enough that any ordinal written here
   * is wrong by the next occurrence. If cinnamon
   * ever migrates to `createRoot`, this ref survives the remount and the
   * paragraph above is what still justifies it.
   */
  const prefetchedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (slug !== slugFromUrl) {
      dispatch(fetchConfig({ slug: slugFromUrl, language }))
    }
  }, [slug, slugFromUrl, dispatch])

  /*
   * The session work is per route, not global (COM-738).
   *
   * Both halves below used to run on every page load, before any route had
   * matched: the session id and the catalog prefetch went out whatever the
   * shopper had opened, and a path carrying no second segment raised a fatal
   * `NonProvidedParams` over it. Nothing expressed that a *particular* route
   * needed a session. `useSessionGuard` derives that from the matched route's
   * own authored template — see its docstring for why the token rather than
   * the value, and why an unmatched path keeps the pre-guard behaviour.
   *
   * **What this changes for a storefront that authors no session-less route is
   * nothing at all**, and that is the whole risk story: every route in
   * `defaultConfig` names `:sessionId`, so `requiresSession` is `true` on every
   * match, and both branches read exactly as they did before. The *value* is
   * also the same one — the guard takes it from the route match's params
   * rather than from `getUrlParams`' positional split, and on
   * `/:slug/:sessionId` those are the same segment by construction.
   *
   * The positional read is what made the old check misleading, and it is worth
   * knowing before touching this: `getUrlParams` splits the pathname and takes
   * index 1, so on a session-less route like `/acme/about` it returned the
   * string `'about'`. `NonProvidedParams` therefore never fired there — the
   * page loaded, with `'about'` dispatched as the session id and a catalog
   * prefetch issued against it. "It loads without the fatal error" is
   * consequently not evidence that this guard works; that a session-less route
   * puts *nothing* in the store is.
   *
   * **The `config &&` on the error branch is the one deliberate timing change,
   * and it is forced rather than chosen.** The requirement is derived from the
   * route table, the route table arrives with the config, and the first render
   * of a real boot has neither — so on `/acme` the pre-config answer can only
   * be the positional one, which finds nothing at index 1 and raises the fatal
   * error on exactly the shape this ticket exists to make loadable. And that
   * first render would be final: nothing *reachable while the error screen is
   * up* clears `globalError`. Say it that way rather than "nothing ever clears
   * it", which is false — `manageErrors` is its only writer and it assigns
   * `state.globalError = !!globalError` unconditionally, so the two callers
   * that omit the flag (`hooks/cart/useGetCart.ts`,
   * `hooks/summary/useGetSummary.ts`) do reset it. Neither can run here:
   * `routes/index.tsx` renders `<ErrorMessage>` *instead of* `<Routes>` while
   * the flag is set, and both callers hang off templates and event handlers
   * mounted below `<Routes>`. An earlier revision of this guard shipped
   * without the conjunct, passed every other test in `sessionGuard.test.tsx`,
   * and did not work at all.
   * The session half of the check therefore waits for the config, as the
   * prefetch half above already did. What that costs an existing storefront is
   * *when* the error appears on a session-less URL, not whether: a path that
   * matches no authored route still falls back to the positional answer and
   * still errors, one config fetch later, under the same `<Splash />` that
   * covers the boot either way. A config fetch that fails raises its own
   * `ErrorOnInitialLoad` from `fetchConfig`, so a failed boot is not silenced
   * by this.
   *
   * **This effect stopped being once-per-page-load when COM-738 landed, and
   * the prefetch gate below is what puts that back (COM-974).** Before that
   * ticket no client-side navigation could move any of the four deps, so the
   * dependency array said "on load" by construction. `useSessionGuard` reads
   * `useLocation()`, which subscribes `Config` to the router, and two of the
   * four — `requiresSession` and `sessionId` — now move whenever the shopper
   * crosses between a route declaring `:sessionId` and one that does not. On
   * `/acme/sess1` -> `/acme/about` -> `/acme/sess1` the outbound leg is
   * harmless, because the requirement going true -> false only re-runs the
   * effect into a branch that suppresses everything; the return leg is not.
   *
   * What the return leg cost is not the duplicate request. Both prefetch
   * actions dispatch `addLoader(LoadersType.INITIAL_DATA)` before their round
   * trip and neither carries an idempotence check of its own;
   * `routes/index.tsx` computes `isBooting` from any loader that is not
   * `CREATE_ORDER`, and while that is set `<Routes>` holds nothing but the
   * `path="*"` catch-all, which sits *outside* the `!isBooting` ternary. So
   * for the length of a `getSession` + `getCatalog` + `getCart` round trip the
   * container the shopper just navigated to never mounts and anything mounted
   * is torn down — COM-858's failure class reached through a different door.
   * `onCartResponse` also re-runs against server state.
   *
   * **The gate is here and not in the two fetch hooks**, because this is where
   * the decision to prefetch is made and it is the only caller of either hook
   * (checked: nothing else imports them outside their own tests). Putting it
   * in the hooks would have to be written twice, and would answer for callers
   * that do not exist yet as if their intent were known.
   *
   * The dependency array is unchanged, and the `eslint-disable` under it stays
   * load-bearing for the reason it always was: both fetch actions are fresh
   * closures on every render, so listing them would re-fire this effect on
   * every render of a component that is now re-rendered by every navigation.
   */
  useEffect(() => {
    if (config && requiresSession) {
      /*
       * Outside the prefetch gate below, deliberately — but **defensive, not
       * load-bearing** (COM-974). Say which, per the COM-736 convention, so
       * this does not read as a behaviour someone has measured.
       *
       * The gate exists for the round trip and for the boot loader that round
       * trip raises; this is a synchronous store write that does neither, and
       * re-running it on the return leg cannot move the store — the reducer
       * assigns the payload verbatim (`redux/slices/config/config.ts`) and the
       * payload is the id already there whenever the gate closes. Keeping it
       * here makes "on a session-bearing route the store holds that route's
       * id" true independently of what the prefetch gate decides, rather than
       * conditional on it.
       *
       * **No test can currently tell the two placements apart, and that is a
       * property of the code rather than a hole in the suite.** Moving this
       * dispatch inside the gate passes the whole suite — measured — because
       * this line is the only `setSessionId` dispatch in the repo outside
       * tests, so once the gate closes nothing else can make the store's id
       * diverge from the key the gate holds. Add a second dispatcher and that
       * stops being true; this placement is what keeps it cheap to reason
       * about when someone does.
       *
       * The session-less branch is untouched by all of this: the block does
       * not run at all there, so an id an earlier route stored survives the
       * crossing, which is what `ANTI-VACUITY: leaves a session id an earlier
       * route stored alone` pins.
       */
      dispatch(setSessionId(sessionId))

      const prefetchKey = JSON.stringify([slugFromUrl, sessionId])
      if (prefetchedKeyRef.current !== prefetchKey) {
        prefetchedKeyRef.current = prefetchKey
        /*
         * Give the pair back when the attempt reports it did not land
         * (COM-974). **Without this the gate turns a recoverable failure into
         * a permanent one**, which is worse than the bug it fixes.
         *
         * `getCatalog` carries `errorPolicy: 'ignore'`, so a catalog resolver
         * that errors calls no `onError`, leaves `catalogData` absent, and
         * skips `onCompleted` — the shopper gets a fully rendered page with an
         * empty catalogue, no error and nothing in Datadog. The ref was
         * already written, so before this callback the pair was recorded as
         * fetched and no in-app action could re-fetch it; only a full page
         * reload could, and a webview may offer none.
         *
         * **The equality check is not defensive.** The two prefetch hooks are
         * `async` and settle a round trip later, by which time the shopper may
         * have crossed to another session and a *newer*, successful key may be
         * in the ref. Clearing unconditionally would discard that one and let
         * the next crossing re-fire a prefetch that had already succeeded —
         * reintroducing this ticket's original bug through the repair for it.
         *
         * **The release cannot produce a retry loop, and that was measured
         * rather than assumed.** A released key means the next effect run
         * prefetches again, and on a `hidePrices` storefront the failed
         * attempt's own `setSessionData` moves `config`'s identity and so
         * re-runs this effect immediately. That costs at most one extra
         * attempt: the identity moves only when `hidePricesActive` actually
         * flips, and Immer's `set` trap short-circuits on an unchanged value,
         * so a second `setSessionData` carrying the same customer leaves
         * `config` identical. Measured directly on the real reducer —
         * `firstMoved: true, secondMoved: false`. Retries after that need a
         * real navigation.
         *
         * Both call sites take the same callback, and both need their own kill
         * case: a `usePaginatedCategories` storefront and a catalog one reach
         * this through different hooks, and wiring only one of them is a hole
         * a shared seam hides rather than closes.
         */
        const releaseKeyIfPrefetchFailed = (prefetched: boolean) => {
          if (!prefetched && prefetchedKeyRef.current === prefetchKey) {
            prefetchedKeyRef.current = null
          }
        }
        if (config.options?.usePaginatedCategories) {
          fetchInitialCategoriesDataAction(sessionId, slugFromUrl).then(
            releaseKeyIfPrefetchFailed,
          )
        } else {
          fetchInitialCatalogDataAction(sessionId, slugFromUrl).then(
            releaseKeyIfPrefetchFailed,
          )
        }
      }
    }
    if (!slugFromUrl || (config && requiresSession && !sessionId)) {
      dispatch(
        manageErrors({
          errorMessageTranslation: ErrorMessageTranslation.NonProvidedParams,
          showErrorTitle: true,
          globalError: true,
        }),
      )
    }
    // eslint-disable-next-line
  }, [config, sessionId, slugFromUrl, requiresSession])

  useEffect(() => {
    if (
      config &&
      (config.analytics?.googleAnalyticsId || process.env.REACT_APP_GOOGLE_ID)
    ) {
      const googleId =
        config.analytics?.googleAnalyticsId || process.env.REACT_APP_GOOGLE_ID
      ReactGA.initialize(googleId || '')
    }
    // eslint-disable-next-line
  }, [config])

  useEffect(() => {
    if (!config) {
      return
    }
    const fbPixelId =
      config.analytics?.fbPixelId || process.env.REACT_APP_FB_PIXEL_ID
    if (!fbPixelId) {
      return
    }

    window.fbq('init', fbPixelId)
  }, [config])

  return children
}

export default Config
